import { useEffect, useState } from "react";
import { TwitterTimelineEmbed } from "react-twitter-embed";
import { Users, Repeat2, Heart, Twitter } from "lucide-react";
import { fetchTokenMeta } from "@/lib/tokenMeta";

interface TokenSocialsEmbedProps {
  /** Pass either a mint address (auto-fetches meta) or a direct twitter URL */
  mint?: string | null;
  twitterUrl?: string | null;
}

export function TokenSocialsEmbed({ mint, twitterUrl: externalUrl }: TokenSocialsEmbedProps) {
  const [resolvedTwitterUrl, setResolvedTwitterUrl] = useState<string | null | undefined>(externalUrl);
  const [metrics, setMetrics] = useState<{ followers: number; retweets: number; likes: number } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [embedReady, setEmbedReady] = useState(false);

  // If a mint is provided, resolve the twitter URL from fetched meta
  useEffect(() => {
    if (externalUrl !== undefined) {
      setResolvedTwitterUrl(externalUrl);
      return;
    }
    if (!mint) return;

    fetchTokenMeta(mint).then((meta) => {
      setResolvedTwitterUrl(meta?.twitter ?? null);
    }).catch(() => setResolvedTwitterUrl(null));
  }, [mint, externalUrl]);

  // Extract twitter handle from url (e.g. https://twitter.com/elonmusk -> elonmusk)
  const handleMatch = resolvedTwitterUrl
    ? resolvedTwitterUrl.match(/(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)/i)
    : null;
  const handle = handleMatch ? handleMatch[1] : null;

  // Fetch social metrics from our proxy endpoint
  useEffect(() => {
    if (!handle) return;
    setMetricsLoading(true);
    fetch(`/api/social-metrics?handle=${handle}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((data) => setMetrics(data))
      .catch(() => setMetrics(null))
      .finally(() => setMetricsLoading(false));
  }, [handle]);

  // Small delay so the embed doesn't flicker on first render
  useEffect(() => {
    if (!handle) return;
    setEmbedReady(false);
    const t = setTimeout(() => setEmbedReady(true), 250);
    return () => clearTimeout(t);
  }, [handle]);

  if (!handle) return null;

  return (
    <div className="flex flex-col gap-3 mx-2 mb-4 rounded-2xl bg-black/50 border border-[#1DA1F2]/20 overflow-hidden shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2 border-b border-white/5">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1DA1F2]/10 border border-[#1DA1F2]/20">
          <Twitter className="size-3.5 text-[#1DA1F2]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#1DA1F2]">
            @{handle}
          </span>
        </div>
        <span className="text-[10px] font-bold text-white/30 ml-auto">Timeline</span>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-3 gap-2 px-3">
        <MetricCard
          icon={<Users className="size-3.5 text-[#1DA1F2]" />}
          label="Followers"
          value={metricsLoading ? "…" : metrics?.followers?.toLocaleString() ?? "—"}
        />
        <MetricCard
          icon={<Repeat2 className="size-3.5 text-emerald-400" />}
          label="RT's"
          value={metricsLoading ? "…" : metrics?.retweets?.toLocaleString() ?? "—"}
        />
        <MetricCard
          icon={<Heart className="size-3.5 text-rose-400" />}
          label="Likes"
          value={metricsLoading ? "…" : metrics?.likes?.toLocaleString() ?? "—"}
        />
      </div>

      {/* Scrollable Twitter Timeline */}
      <div className="h-[480px] overflow-y-auto rounded-b-2xl relative bg-[#0a0a0a]">
        {embedReady && (
          <TwitterTimelineEmbed
            sourceType="profile"
            screenName={handle}
            theme="dark"
            noHeader
            noFooter
            noBorders
            transparent
            options={{ height: 480 }}
          />
        )}
        {!embedReady && (
          <div className="flex items-center justify-center h-full gap-2 text-white/30">
            <div className="size-4 rounded-full border-2 border-[#1DA1F2]/30 border-t-[#1DA1F2] animate-spin" />
            <span className="text-xs font-medium">Loading timeline…</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-2.5 px-2 rounded-xl bg-white/[0.03] border border-white/5">
      {icon}
      <span className="text-white/90 font-black text-sm mt-1">{value}</span>
      <span className="text-[9px] uppercase tracking-widest text-white/35 mt-0.5">{label}</span>
    </div>
  );
}
